// client/src/components/event/UGCCampaignConfig.jsx
/**
 * =================================================================
 * UGC Campaign Configuration Component
 * =================================================================
 * Follows ChallengeConfig pattern for consistency.
 * FEATURES:
 * 1. Set hashtag, content guidelines
 * 2. Schedule start/end date
 * 3. Display shareable hyperlink
 * 4. View submissions with feature toggle
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import DateTimeSelect from "../DateTimeSelect";
import "./AuctionConfig.css";
import ActionPostButton from "./ActionPostButton";

const UGCCampaignConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "UGC Campaign",
    hashtag: "",
    description: "",
    content_guidelines: "",
    start_at: "",
    end_at: "",
    status: "NEW",
  });

  const [submissions, setSubmissions] = useState([]);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [configExists, setConfigExists] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = configExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=ugc_campaign`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.ugc_campaign) {
        const c = response.data.ugc_campaign;
        setConfigExists(true);
        setConfig({
          name: c.name || itemName || "UGC Campaign",
          hashtag: c.hashtag || "",
          description: c.description || "",
          content_guidelines: c.content_guidelines || "",
          start_at: formatDateForInput(c.start_at),
          end_at: formatDateForInput(c.end_at),
          status: c.status || "NEW",
        });
        loadSubmissionCount();
      } else {
        setConfigExists(false);
        setMessage({
          text: "New UGC Campaign: fill in the form below, then save.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading UGC Campaign config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "New UGC Campaign: fill in the form below, then save.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load UGC Campaign configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/submissions`
      );
      if (response.data.success) {
        setSubmissionCount(response.data.count || response.data.submissions?.length || 0);
      }
    } catch { /* Non-critical */ }
  };

  const loadSubmissions = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/submissions`
      );
      if (response.data.success) {
        setSubmissions(response.data.submissions);
        setSubmissionCount(response.data.count || response.data.submissions.length);
        setShowSubmissions(true);
      }
    } catch (err) {
      console.error("Error loading submissions:", err);
      setMessage({ text: "Failed to load submissions", type: "error" });
    }
  };

  const handleFeature = async (submissionId) => {
    try {
      await axios.post(
        `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/feature/${submissionId}`
      );
      setMessage({ text: "Content featured!", type: "success" });
      setTimeout(() => setMessage({ text: "", type: "" }), 2000);
      loadSubmissions();
    } catch (err) {
      console.error("Error featuring content:", err);
      setMessage({ text: "Failed to feature content", type: "error" });
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("Campaign name is required");
    }
    if (config.start_at && config.end_at) {
      if (new Date(config.end_at) <= new Date(config.start_at)) {
        errors.push("End date must be after start date");
      }
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        hashtag: config.hashtag || null,
        description: config.description || null,
        content_guidelines: config.content_guidelines || null,
        start_at: formatDateTimeForBackend(config.start_at),
        end_at: formatDateTimeForBackend(config.end_at),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setConfigExists(true);
        setMessage({ text: "UGC Campaign configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving UGC Campaign config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save UGC Campaign configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Cancel this UGC Campaign? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/config`,
        { ...config, status: "CANCELLED" }
      );
      if (response.data.success) {
        setMessage({ text: "UGC Campaign cancelled successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error cancelling UGC Campaign:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to cancel UGC Campaign",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading UGC Campaign configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE" || config.status === "CANCELLED";

  return (
    <div className="auction-config-container">
      <div className="auction-config-header">
        <h2>UGC Campaign Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link for participants to submit their content:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {(config.name || itemName || "").replace(/\s+/g, "_")}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
            <ActionPostButton
              tenantId={tenantId}
              eventId={eventId}
              itemId={itemId}
              actionType="ugc_campaign"
              config={config}
              itemName={itemName}
              link={shareLink}
            />
          </div>
        </div>
      )}

      {configExists && (
        <div className="auction-stats-section">
          <h3>Campaign Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Submissions</div>
              <div className="stat-value">{submissionCount}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button type="button" className="view-entries-button" onClick={loadSubmissions}>
              View Submissions
            </button>
          </div>
        </div>
      )}

      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="name">
            Campaign Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={config.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="Enter campaign name"
            disabled={isComplete}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="hashtag">Hashtag</label>
          <input
            type="text"
            id="hashtag"
            value={config.hashtag}
            onChange={(e) => handleInputChange("hashtag", e.target.value)}
            placeholder="#YourCampaignHashtag"
            disabled={isComplete}
          />
          <span className="form-hint">Participants will use this hashtag when sharing content</span>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={config.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe the campaign and what kind of content you're looking for"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="content_guidelines">Content Guidelines</label>
          <textarea
            id="content_guidelines"
            value={config.content_guidelines}
            onChange={(e) => handleInputChange("content_guidelines", e.target.value)}
            placeholder="Rules and guidelines for content submissions"
            disabled={isComplete}
            rows={3}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_at">Start Date & Time</label>
            <DateTimeSelect
              id="start_at"
              value={config.start_at}
              onChange={(v) => handleInputChange("start_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end_at">End Date & Time</label>
            <DateTimeSelect
              id="end_at"
              value={config.end_at}
              onChange={(v) => handleInputChange("end_at", v)}
              tenantId={tenantId}
              disabled={isComplete}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              This campaign is {config.status === "CANCELLED" ? "cancelled" : "complete"} - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {configExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Cancel Campaign
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {showSubmissions && (
        <div className="modal-overlay" onClick={() => setShowSubmissions(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Submissions ({submissions.length})</h3>
              <button className="close-button" onClick={() => setShowSubmissions(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Content</th>
                    <th>Platform</th>
                    <th>Featured</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub, index) => (
                    <tr key={sub.id}>
                      <td>{index + 1}</td>
                      <td>{sub.name}</td>
                      <td>{sub.email}</td>
                      <td>
                        {sub.content_url ? (
                          <a href={sub.content_url} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        ) : (
                          sub.content_text || "—"
                        )}
                      </td>
                      <td>{sub.platform || "—"}</td>
                      <td>{sub.featured ? "Yes" : "No"}</td>
                      <td>{new Date(sub.created_on).toLocaleString()}</td>
                      <td>
                        {!sub.featured && (
                          <button
                            type="button"
                            className="save-button"
                            style={{ padding: "4px 8px", fontSize: "12px" }}
                            onClick={() => handleFeature(sub.id)}
                          >
                            Feature
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UGCCampaignConfig;
